<template>
    <div class="home hr-tile">
        <user-profile-tabs :is-own-profile="isOwnProfile"></user-profile-tabs>
    </div>
    <div v-if="recentSubmissions" class="home hr-tile">
        <h2 class="title is-4">Your Recent Submissions</h2>
        <div class="submission-entries">
            <div class="submission-entry" v-if="!recentSubmissions || recentSubmissions.length == 0">No recent submissions!</div>
            <div v-for="sub in recentSubmissions" :key="sub.Id" class="submission-entry">
                <p>{{sub.Description}} - {{sub.SubDescription}}</p>
                <p>
                    <a target="_blank"  rel="noopener noreferrer" v-if="sub.Evidence" :href="sub.Evidence" class="timecode">{{$filters.durationDisplay(sub.Duration)}}</a>
                    <span v-else class="timecode">{{$filters.durationDisplay(sub.Duration)}}</span>
                    <span class="muted"> submitted </span>
                    {{sub.SubmittedAt}}
                </p>
                <p>
                    <strong>Status:</strong>
                    {{sub.Status}}
                </p>
                <p v-if="sub.Status == 'Pending'">
                    <button class="button is-danger" v-on-async="() => retractSubmission(sub)">Retract</button>
                </p>
                <p v-if="sub.Reason">
                    <strong>Reason:</strong> {{sub.Reason}}
                </p>
            </div>
        </div>
    </div>

</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import { Prop, Watch } from "vue-facing-decorator";
import ProfileInfo from "@/components/User/ProfileInfo.vue";
import {
  Claim,
    Game,
    RunSubmission,
    RunSubmissionsService,
    RunSubmissionStatus,
    UserCareer,
    UserProfile
} from "@/api";
import OnAsync from "@/directives/OnAsync";
import { GameStore } from "@/store/games";
import TimeService from "@/services/TimeService";
import UserProfileTabs from "@/components/User/UserProfileTabs.vue";
import UserCareerGame from "./UserCareerGame.vue";
import { ContentLoader } from "vue-content-loader";
import Masonry from "@/directives/Masonry";
import { useToast } from "vue-toastification";

interface ResolvedRunSubmission {
    Description: string;
    SubDescription: string;
    Duration?: number;
    Evidence?: string | null;
    SubmittedAt?: string;
    Status?: RunSubmissionStatus;
    Reason?: string | null;
    Id: string | null;
}

@Component({
    components: {
        ProfileInfo,
        UserProfileTabs,
        UserCareerGame,
        ContentLoader
    },
    directives: {
        OnAsync,
        Masonry
    }
})
export default class RecentDetails extends Vue {
    public recentSubmissions: ResolvedRunSubmission[] | null = null;
    public games: Game[] = new Array();

    chosenCategory: string | null = null;
    toast = useToast();
    levelSearch: string | null = null;
    canEditRuns = false;

    @Prop()
    public user: UserProfile | null = null;

    @Prop()
    public career: UserCareer | null = null;

    @Prop()
    public isOwnProfile: boolean = false;

    get catOrder() {
        return Object.keys(this.career?.RunsByCategory!).sort((a, b) => {  
            if (a == "Solo") return -1;
            if (b == "Solo") return 1;
            if (a == "Coop") return -1;
            if (b == "Coop") return 1;
            return 0;
        });
    }

    async created() {
        this.games = (await GameStore.get()).games;

        if (this.isOwnProfile) {
            await this.showRecentSubmissions();
        }
    }

    async mounted() {
        await this.load();
    }

    @Watch("career")
    public async watchCareer() {
        await this.load();
    }

    async load() {
        var hrClaims = this.$store.state.auth.hrClaimStrings;

        this.canEditRuns = (this.$store.state.auth.claims.userId == this.user?.UserId && hrClaims.indexOf(Claim.SELF_READ_WRITE) > 0)
            || hrClaims.indexOf(Claim.MODIFY_RUNS_GLOBAL) > 0
            || hrClaims.indexOf(Claim.MODIFY_RUNS_LIMITED) > 0;

        if(this.career?.RunsByCategory == null) return;

        this.chosenCategory = this.catOrder[0];
    }

    get totalPoints() {
        if(this.career?.RankInfoByCategory == null) return;

        return Object.values(this.career.RankInfoByCategory).reduce((a,r) => a + (r?.Points! || 0), 0)
    }

    get totalTrophies() {
        if(this.career?.RankInfoByCategory == null) return;

        return Object.values(this.career.RankInfoByCategory).reduce((a,r) => a + (r?.GoldCount! || 0) + (r?.SilverCount! || 0) + (r?.BronzeCount! || 0), 0)
    }

    get categoryGames() {
        return this.games.filter(g => g.Categories && g.Categories.filter(c => c.Name == this.chosenCategory).length > 0);
    }

    @Watch("isOwnProfile")
    async showRecentSubmissions() {
        if(this.isOwnProfile == false) {
            this.recentSubmissions = null;
            return;
        }

        let rawSubmissions = await RunSubmissionsService.getRecentSubmissions();
        if (!rawSubmissions || rawSubmissions.length == 0) {
            this.recentSubmissions = null;
        }

        let resolvedSubs = new Array() as ResolvedRunSubmission[];

        for (let sub of rawSubmissions) {
            var game = this.games.find((g) => g.Id == sub.GameId);
            if (!game?.Categories || !game.RunnableSegments || !game.Difficulties) continue;
            var cat = game.Categories.find((c) => c.Id == sub.CategoryId);
            var seg = game.RunnableSegments.find((s) => s.Id == sub.RunnableSegmentId);
            var diff = game.Difficulties.find((s) => s.Id == sub.DifficultyId);
            if (!cat || !seg || !diff) continue;

            let desc = `${game.Name} ${diff.Name}`;
            let subdesc = `${seg.Name} - ${cat.Name}`;

            resolvedSubs.push({
                Description: desc,
                SubDescription: subdesc,
                Duration: sub.Duration,
                Evidence: sub.Participants![0].EvidenceLink,
                SubmittedAt: TimeService.iso8601ToShortFormat(sub.SubmittedAt!),
                Status: sub.Status,
                Reason: sub.Reason,
                Id: sub.Id!
            });
        }

        this.recentSubmissions = resolvedSubs;
    }

    public async retractSubmission(submission: ResolvedRunSubmission) {
        if(await this.$confirm({
            message: "Are you sure you want to retract/delete this submission? It will no longer show for moderators and will be removed permanently."
        })) {
            await RunSubmissionsService.retractSubmission(submission.Id!);
            await this.load();
            await this.showRecentSubmissions();
        }
    }
}
</script>

<style lang="scss" scoped>
@use "@/assets/haloruns_vars.scss" as *;

.submission-entries {
    @include hr-tile-full-bleed;
    border-top: 2px solid $border-color;

    .submission-entry {
        border-bottom: 1px solid $border-color;
        margin-bottom: 5px;
        padding: 5px 15px;
    }
}

.career-details {
    .inline-select select {
        font-size: 1.5rem;
        height: 2.4rem;
        letter-spacing: 0.1rem;
        font-weight: 500;
    }

    .inline-select.select::after {
        right: 0.6rem;
    }

    .inline-select:not(.is-multiple) {
        height: 1.8rem;
    }

    .game-symbol {
        margin: 0 0.25rem;
        text-transform: uppercase;
        font-weight: bold;
        color: #fff;
        opacity: 0.25;

        &:first-child {
            margin-left: 0;
        }

        &.submitted {
            opacity: 1;
        }
    }
}



</style>